'use client';

import { type ChangeEvent, type ChangeEventHandler, type HTMLAttributes, type ReactNode, type Ref, useCallback, useId } from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { clsx } from 'clsx';
import CheckSmallIcon from '../../../icons/react/icon-check-small';
import CheckTinyIcon from '../../../icons/react/icon-check-tiny';
import MinusIcon from '../../../icons/react/icon-minus';
import { HorizontalStack, type HorizontalStackAlign, type HorizontalStackBlockAlign } from '../../alignment/horizontal-stack';
import { VerticalStack } from '../../alignment/vertical-stack';
import { ErrorMessage } from '../error-message/error-message';
import { InputHint } from '../input-hint/input-hint';
import inputStyles from '../input.module.scss';
import styles from './checkbox-input.module.scss';

type CheckboxInputSize = 'small' | 'medium';

type CheckboxInputLabelSide = 'left' | 'right';

export interface CheckboxInputProps extends Omit<HTMLAttributes<HTMLInputElement>, 'type'> {
  align?: HorizontalStackAlign;
  blockAlign?: HorizontalStackBlockAlign;
  checked: boolean;
  disabled?: boolean;
  error?: string;
  hideLabel?: boolean;
  hint?: ReactNode;
  indeterminate?: boolean;
  label: ReactNode;
  labelSide?: CheckboxInputLabelSide;
  name: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  ref?: Ref<HTMLInputElement>;
  required?: boolean;
  size?: CheckboxInputSize;
}

export function CheckboxInput({
  align = 'start',
  blockAlign = 'center',
  className,
  checked,
  disabled,
  error,
  hideLabel,
  hint,
  id: providedId,
  indeterminate,
  label,
  labelSide = 'right',
  name,
  onChange,
  size = 'medium',
  ...rest
}: CheckboxInputProps) {
  const generatedId = useId();
  const id = providedId || generatedId;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const showError = Boolean(error);

  const handleCheckChange = useCallback(
    (checked: boolean) => {
      onChange?.({ target: { checked, name, value: checked as any } } as ChangeEvent<HTMLInputElement>);
    },
    [name, onChange],
  );

  const labelElement = (
    <label className={clsx(styles.label, hideLabel && styles.isHidden)} htmlFor={id}>
      {label}
    </label>
  );

  return (
    <VerticalStack
      className={clsx(inputStyles.input, styles.checkboxInput, styles[`size-${size}`], disabled && inputStyles.isDisabled, className)}
      gap={2}
    >
      <HorizontalStack align={align} blockAlign={blockAlign} gap={size === 'small' ? 2 : 4} wrap="nowrap">
        {labelSide === 'left' && labelElement}
        <CheckboxPrimitive.Root
          checked={indeterminate ? 'indeterminate' : checked}
          className={styles.checkbox}
          disabled={disabled}
          id={id}
          onCheckedChange={handleCheckChange}
          {...(rest as CheckboxPrimitive.CheckboxProps)}
        >
          <CheckboxPrimitive.Indicator>
            {size === 'small' ? (
              indeterminate ? (
                <MinusIcon aria-hidden className={styles.indicator} height={16} width={16} />
              ) : (
                <CheckTinyIcon aria-hidden className={styles.indicator} height={16} width={16} />
              )
            ) : indeterminate ? (
              <MinusIcon aria-hidden className={styles.indicator} height={16} width={16} />
            ) : (
              <CheckSmallIcon aria-hidden className={styles.indicator} height={16} width={16} />
            )}
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
        {labelSide === 'right' && labelElement}
      </HorizontalStack>

      {hint ? <InputHint id={hintId}>{hint}</InputHint> : null}
      {showError ? <ErrorMessage id={errorId}>{error}</ErrorMessage> : null}
    </VerticalStack>
  );
}
